/**
 * API for DigitalTwin MVP.
 *
 * GET    /v1/digitaltwins/sites                          — sites that have digital twins
 * GET    /v1/digitaltwins?siteId={siteId}                — digital twin list for a site
 * PUT    /v1/digitaltwins/{siteSystemId}                 — update layout, overlay, order
 * GET    /v1/digitaltwins/equipment?siteId={siteId}      — equipment list for palette
 * GET    /v1/digitaltwins/latest-readings?equipmentIds=  — latest readings per equipment
 */
import { withBlankBaseLayout } from '../blankBaseLayout';
import type {
  IDigitalTwin,
  IDigitalTwinDTO,
  IDigitalTwinSite,
  IUpdateDigitalTwinRequest,
  IOverlayItem,
  ISegment,
  IEquipmentSystemGroup,
  IEquipmentReading,
} from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parses the serialised overlay string into items + segments.
 * Supports both the legacy format (plain IOverlayItem[] array) and the current
 * format ({ items: IOverlayItem[], segments: ISegment[] }).
 */
function parseOverlay(raw: string): { items: IOverlayItem[]; segments: ISegment[] } {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { items: parsed as IOverlayItem[], segments: [] };
    }
    return {
      items: Array.isArray(parsed.items) ? (parsed.items as IOverlayItem[]) : [],
      segments: Array.isArray(parsed.segments) ? (parsed.segments as ISegment[]) : [],
    };
  } catch {
    return { items: [], segments: [] };
  }
}

function mapDto(dto: IDigitalTwinDTO): IDigitalTwin {
  const { items, segments } = parseOverlay(dto.overlay);
  return {
    id: dto.id,
    siteSystemId: dto.siteSystemId,
    baseLayout: withBlankBaseLayout(dto.baseLayout),
    overlay: items,
    segments,
    displayOrder: dto.displayOrder,
  };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * GET /v1/digitaltwins/sites
 * Returns the list of sites that have at least one digital twin.
 */
export const getDigitalTwinSites = async (): Promise<IDigitalTwinSite[]> => {
  const response = await window.axios.get<{ data: IDigitalTwinSite[] }>(
    '/v1/digitaltwins/sites'
  );
  return response.data.data ?? [];
};

/**
 * GET /v1/digitaltwins?siteId={siteId}
 * Returns the list of digital twins for a site, with overlay parsed from JSON string.
 */
export const getDigitalTwins = async (
  siteId: number
): Promise<IDigitalTwin[]> => {
  const response = await window.axios.get<{ data: IDigitalTwinDTO[] }>(
    `/v1/digitaltwins?siteId=${siteId}`
  );
  return (response.data.data ?? []).map(mapDto);
};

// ---------------------------------------------------------------------------
// Equipment endpoint
// ---------------------------------------------------------------------------

/**
 * GET /v1/digitaltwins/equipment?siteId={siteId}
 * Returns grouped equipment list for the site palette.
 */
export const getEquipmentGroups = async (
  siteId: number
): Promise<IEquipmentSystemGroup[]> => {
  const response = await window.axios.get<{ data: IEquipmentSystemGroup[] }>(
    `/v1/digitaltwins/equipment?siteId=${siteId}`
  );
  return response.data.data ?? [];
};

// ---------------------------------------------------------------------------
// Readings endpoint
// ---------------------------------------------------------------------------

/**
 * GET /v1/digitaltwins/latest-readings?equipmentIds={id1}&equipmentIds={id2}...
 * Returns latest readings for the given equipment IDs.
 */
export const getLatestReadings = async (
  equipmentIds: number[]
): Promise<IEquipmentReading[]> => {
  const params = new URLSearchParams();
  equipmentIds.forEach((id) => params.append('equipmentIds', String(id)));
  const response = await window.axios.get<{ data: IEquipmentReading[] }>(
    `/v1/digitaltwins/latest-readings?${params.toString()}`
  );
  return response.data.data ?? [];
};

/**
 * PUT /v1/digitaltwins/{id}
 * Updates an existing digital twin (baseLayout, overlay, displayOrder).
 * Payload sent as multipart/form-data; baseLayout is optional (omit to keep existing).
 * Returns the updated digital twin.
 */
export const updateDigitalTwin = async (
  siteSystemId: number,
  payload: IUpdateDigitalTwinRequest
): Promise<IDigitalTwin> => {
  const form = new FormData();
  if (payload.baseLayout) form.append('baseLayout', payload.baseLayout);
  form.append('overlay', payload.overlay);
  form.append('displayOrder', String(payload.displayOrder));
  const response = await window.axios.put<{ data: IDigitalTwinDTO }>(
    `/v1/digitaltwins/${siteSystemId}`,
    form
  );
  return mapDto(response.data.data);
};
